import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, ArrowRight, UserPlus, LogIn, Gift } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import PasswordStrengthChecklist, { passwordIsStrong } from "@/components/auth/PasswordStrengthChecklist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

export default function ClientPortal() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [referralPreview, setReferralPreview] = useState(null);  // { referrer_name, current_percentage }
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Pick up ?ref=CODE / ?tab=register in the URL so shared invite links and
  // header CTAs both land users straight on the right form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const tab = params.get("tab");
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setMode("register");  // a referral link is an invitation to sign up
    } else if (tab === "register" || tab === "signup") {
      setMode("register");
    }
  }, []);

  // Validate the referral code as the user types/pastes (debounced)
  useEffect(() => {
    const code = referralCode.trim().toUpperCase();
    if (code.length !== 12) {
      setReferralPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/referral/lookup/${code}`);
        setReferralPreview(res.data);
      } catch {
        setReferralPreview({ invalid: true });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [referralCode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter your credentials."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (res.data.access_token) {
        localStorage.setItem("auth_token", res.data.access_token);
      }
      if (res.data.role === "admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/vip/dashboard");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Invalid credentials. Please verify your email and password."));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error("Please fill in all required fields."); return; }
    if (!passwordIsStrong(password)) {
      toast.error("Password must satisfy all the rules below.");
      return;
    }
    setLoading(true);
    try {
      const payload = { name, email, password, phone: phone || null };
      if (referralCode.trim()) payload.referral_code = referralCode.trim().toUpperCase();
      const res = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      localStorage.setItem("auth_email", email);
      if (res.data?.referral_code) {
        toast.success(`Welcome — your referral code is ${res.data.referral_code}`);
      } else {
        toast.success("Welcome to the VIP portal.");
      }
      localStorage.setItem("auth_token", "pending");
      navigate("/vip/dashboard");
    } catch (err) {
      toast.error(errorMessage(err, "Registration failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="client-portal-page" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
      <motion.div initial="hidden" animate="visible" className="w-full max-w-md px-6">
        <motion.div variants={fadeUp} custom={0} className="text-center mb-10">
          <Crown size={36} className="text-gold mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-white mb-2">VIP Client Portal</h1>
          <p className="text-white/40 font-body text-sm">Access your exclusive galleries, media, and invoices</p>
        </motion.div>

        {/* Tab Toggle */}
        <motion.div variants={fadeUp} custom={1} className="flex mb-8">
          <button
            data-testid="portal-login-tab"
            onClick={() => setMode("login")}
            className={`flex-1 py-3 text-sm font-body text-center transition-all border-b-2 ${
              mode === "login" ? "border-gold text-gold" : "border-white/10 text-white/40 hover:text-white/60"
            }`}
          >
            <LogIn size={16} className="inline mr-2" />Sign In
          </button>
          <button
            data-testid="portal-register-tab"
            onClick={() => setMode("register")}
            className={`flex-1 py-3 text-sm font-body text-center transition-all border-b-2 ${
              mode === "register" ? "border-gold text-gold" : "border-white/10 text-white/40 hover:text-white/60"
            }`}
          >
            <UserPlus size={16} className="inline mr-2" />Sign up with email
          </button>
        </motion.div>

        {mode === "login" ? (
          <motion.form key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleLogin} className="space-y-5" data-testid="client-login-form">
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Email</label>
              <Input
                data-testid="client-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Password</label>
              <Input
                data-testid="client-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="client-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
            >
              {loading ? "Authenticating..." : "Access Portal"} <ArrowRight size={16} />
            </button>
          </motion.form>
        ) : (
          <motion.form key="register" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleRegister} className="space-y-5" data-testid="client-register-form">
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Full Name *</label>
              <Input
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Email *</label>
              <Input
                data-testid="register-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Phone</label>
              <Input
                data-testid="register-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (876) 000-0000"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Password *</label>
              <Input
                data-testid="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a secure password"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
              />
              <PasswordStrengthChecklist password={password} data-testid="client-password-rules" />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block flex items-center gap-1.5">
                <Gift size={11} className="text-gold" /> Referral Code (optional)
              </label>
              <Input
                data-testid="register-referral-code"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="12 characters — e.g. B8GWPVTPM7CV"
                maxLength={12}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm font-mono tracking-wider"
              />
              {referralPreview?.invalid && (
                <p className="text-red-400/80 text-[11px] font-body mt-1.5" data-testid="referral-invalid">
                  That code doesn't match any account.
                </p>
              )}
              {referralPreview && !referralPreview.invalid && (
                <p className="text-gold text-[11px] font-body mt-1.5 flex items-center gap-1.5" data-testid="referral-valid">
                  <Gift size={10} /> {referralPreview.referrer_name} invited you — they'll get {referralPreview.current_percentage}% off their next package.
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="client-register-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
            >
              {loading ? "Creating Account..." : "Create VIP Account"} <ArrowRight size={16} />
            </button>
          </motion.form>
        )}

        <motion.p variants={fadeUp} custom={3} className="text-white/20 text-xs font-body text-center mt-8">
          Your data is protected with enterprise-grade encryption.
        </motion.p>
      </motion.div>
    </div>
  );
}
